import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TilesService } from '@core/services/tiles.service';
import { DevicesService } from '@core/services/devices.service';
import { PermissionsService } from '@core/services/permissions.service';
import { SessionService } from '@core/services/session.service';
import { Tile, TileId } from '@shared/models/tile';
import { SimpleAnalogStructure } from '@shared/models/structure';
import { Device, DeviceId, DeviceType } from '@shared/models/device';
import {
  FormulaBinding,
  FormulaBindingSource,
  FormulaConfig,
  FormulaType,
  makeDevicePointTile,
  makeFormulaDevice,
  makeInitialFormulaTile,
} from '@shared/models/formula-device';
import { Permission } from '@shared/models/fga/permission';
import { ObjectType } from '@shared/models/object.model';
import { FormulaTemplate } from '../../shared/constants/formula-templates.constants';

@Injectable({ providedIn: 'root' })
export class NumericTileCreatorService {
  constructor(
    private tilesService: TilesService,
    private devicesService: DevicesService,
    private permissionsService: PermissionsService,
    private sessionService: SessionService,
  ) {}

  createDeviceTile$(name: string, deviceId: DeviceId, pointId: string, protocol: DeviceType): Observable<TileId> {
    return this.tilesService.createTile$(makeDevicePointTile(name, deviceId, pointId, protocol));
  }

  createFormulaTile$(
    name: string,
    formula: FormulaTemplate,
    bindings: Record<string, Partial<FormulaBindingSource>>,
  ): Observable<TileId> {
    return this.buildFormulaTile$(name, FormulaType.Template, formula.id, formula.variables.map(v => v.key), bindings);
  }

  private checkPermissions$(deviceIds: DeviceId[]): Observable<boolean> {
    if (!deviceIds.length) return of(true);
    return forkJoin(
      deviceIds.map(id =>
        this.permissionsService.hasPermissions({
          UserID: this.sessionService.userId,
          Permissions: [Permission.CAN_CREATE_TILES],
          ObjectID: id,
          ObjectType: ObjectType.DEVICE,
        }),
      ),
    ).pipe(map(results => results.every(r => r)));
  }

  private createInitialTile$(name: string): Observable<TileId> {
    return this.tilesService.createTile$(makeInitialFormulaTile(name));
  }

  createCustomFormulaTile$(
    expression: string,
    variables: string[],
    bindings: Record<string, Partial<FormulaBindingSource>>,
  ): Observable<TileId> {
    return this.buildFormulaTile$('Custom Formula', FormulaType.Custom, expression, variables, bindings);
  }

  cloneFormulaTile$(original: Tile, clone: Tile): Observable<TileId> {
    const source = original.Structure as SimpleAnalogStructure;

    return this.devicesService.fetchDevice$(source.DeviceID).pipe(
      switchMap(device => {
        const points = device.Config?.Formula?.Points ?? [];
        const point = points.find(p => p.ID === source.PointID) ?? points[0];

        if (!point) {
          return throwError(() => new Error('The formula for this tile could not be found.'));
        }

        const bindings: Record<string, Partial<FormulaBindingSource>> = Object.fromEntries(
          point.Bindings.map(b => [b.Variable, b.Source]),
        );

        return this.checkPermissions$(this.uniqueDeviceIds(bindings)).pipe(
          switchMap(allowed => {
            if (!allowed) {
              return throwError(() => new Error('You do not have permission to add tiles to one or more of the formula input devices.'));
            }

            const cloneStructure = clone.Structure as SimpleAnalogStructure;
            cloneStructure.DeviceID = '';
            cloneStructure.PointID = '';

            return this.tilesService.createTile$(clone);
          }),
          switchMap(tileId =>
            this.createFormulaDevice$(tileId, point.Formula.Type, point.Formula.Value, point.Bindings.map(b => b.Variable), bindings).pipe(
              switchMap(newDevice => this.updateTileDevice$(tileId, newDevice.ID!).pipe(
                catchError(error => this.rollback$(this.devicesService.deleteDevice$(newDevice.ID!), error)),
              )),
              catchError(error => this.rollback$(this.tilesService.deleteTile$(tileId), error)),
            ),
          ),
        );
      }),
    );
  }

  private rollback$(cleanup$: Observable<unknown>, error: unknown): Observable<never> {
    return cleanup$.pipe(
      catchError(() => of(null)),
      switchMap(() => throwError(() => error)),
    );
  }

  private buildFormulaTile$(
    name: string,
    type: FormulaType,
    value: string,
    variableKeys: string[],
    bindings: Record<string, Partial<FormulaBindingSource>>,
  ): Observable<TileId> {
    return this.checkPermissions$(this.uniqueDeviceIds(bindings)).pipe(
      switchMap(allowed => {
        if (!allowed) {
          return throwError(() => new Error('You do not have permission to add tiles to one or more selected devices.'));
        }
        return this.createInitialTile$(name);
      }),
      switchMap(tileId =>
        this.createFormulaDevice$(tileId, type, value, variableKeys, bindings).pipe(
          map(device => ({ tileId, deviceId: device.ID! })),
        ),
      ),
      switchMap(({ tileId, deviceId }) => this.updateTileDevice$(tileId, deviceId)),
    );
  }

  private createFormulaDevice$(
    tileId: TileId,
    type: FormulaType,
    value: string,
    variableKeys: string[],
    bindings: Record<string, Partial<FormulaBindingSource>>,
  ): Observable<Device> {
    const formulaBindings: FormulaBinding[] = variableKeys
      .filter(k => bindings[k]?.DeviceID && bindings[k]?.PointID)
      .map(k => ({ Variable: k, Source: bindings[k] as FormulaBindingSource }));

    const formulaConfig: FormulaConfig = {
      Points: [{
        ID: '',
        Formula: { Type: type, Value: value },
        Bindings: formulaBindings,
        Principals: { TileIDs: [tileId] },
      }],
    };

    return this.devicesService.createDevice$({ ...makeFormulaDevice(formulaConfig), Owner: this.sessionService.userId });
  }

  private updateTileDevice$(tileId: TileId, deviceId: DeviceId): Observable<TileId> {
    const tile = this.tilesService.sessUserTiles[tileId];
    const updated = Tile.create(tile);
    (updated.Structure as SimpleAnalogStructure).DeviceID = deviceId;
    (updated.Structure as SimpleAnalogStructure).PointID = '';
    return this.tilesService.saveTile$(updated).pipe(map(() => tileId));
  }

  private uniqueDeviceIds(bindings: Record<string, Partial<FormulaBindingSource>>): DeviceId[] {
    return [...new Set(
      Object.values(bindings).map(b => b.DeviceID).filter((id): id is string => !!id),
    )];
  }
}
